'use client';

import { toast } from '@/app/_components/core/toast';
import { useLabel } from '@/app/_contexts/labels-context';
import { useDownload } from '@/app/_hooks/use-download';
import type { ReactNode, MouseEvent, KeyboardEvent } from 'react';

export interface DownloadLinkProps {
  /** The URL to download from */
  href: string;
  /** The filename for the downloaded file */
  filename: string;
  /** Link content */
  children: ReactNode;
  /** Additional class names */
  className?: string;
  /** Icon to display after the children */
  endIcon?: ReactNode;
  /** Callback when download succeeds */
  onSuccess?: () => void;
  /** Callback when download fails */
  onError?: (error: Error) => void;
  /** Passive, code-assigned tracking key rendered as `data-track-id`. */
  'data-track-id'?: string;
}

/**
 * DownloadLink - Client component for file downloads without loading UI.
 *
 * Shows only toast notifications on success/error, no visual changes to the link.
 * Used for ghost/link button variants and text links.
 *
 * Features:
 * - No loading state (link appearance unchanged)
 * - Toast notifications for success/error
 * - Keyboard accessible (Enter + Space)
 * - iOS Safari: Opens file in new tab (no toast since we can't confirm success)
 */
export function DownloadLink({
  href,
  filename,
  children,
  className,
  endIcon,
  onSuccess,
  onError,
  'data-track-id': dataTrackId,
}: DownloadLinkProps) {
  const successLabel = useLabel('downloadSuccess', 'Download erfolgreich');
  const errorLabel = useLabel('downloadError', 'Download fehlgeschlagen');

  const { isIOSSafari, download } = useDownload({
    onSuccess: () => {
      // No toast for iOS Safari - we don't know if download actually succeeded
      if (!isIOSSafari) {
        toast.success(successLabel);
      }
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(errorLabel, {
        description: error.message,
      });
      onError?.(error);
    },
  });

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    void download(href, filename);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => {
    // Support both Enter and Space for accessibility
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void download(href, filename);
    }
  };

  return (
    <a
      href={href}
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      download={filename}
      aria-label={`Download ${filename}`}
      data-track-id={dataTrackId}
    >
      {children}
      {endIcon && (
        <span className="inline-flex shrink-0 items-center">
          {endIcon}
        </span>
      )}
    </a>
  );
}
